import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { supabase } from '@/lib/supabase';

// Replaces the Base44 AuthContext. Gone with it:
//   - app-params.js (token read from the URL into localStorage)
//   - the /api/apps/public "public settings" probe
//   - base44.auth.redirectToLogin (routing is ours now)
//
// The session is managed by supabase-js: stored, refreshed, and broadcast
// through onAuthStateChange. We layer the profile row on top, because role
// and membership live there rather than in the auth token.

const AuthContext = createContext(null);

// The signup trigger raises an exception for addresses that are neither
// @stellarlab.tech nor invited. Supabase surfaces that as a generic database
// error, so we translate it into something a person can act on.
function readableAuthError(error) {
  if (!error) return null;
  const raw = (error.message || '').toLowerCase();

  if (raw.includes('database error') || raw.includes('unexpected_failure')) {
    return 'This app is limited to stellarlab.tech accounts and invited users. Ask an administrator for an invitation.';
  }
  if (raw.includes('invalid login credentials')) {
    return 'Incorrect email or password.';
  }
  if (raw.includes('email not confirmed')) {
    return 'Please confirm your email address first. Check your inbox for the link.';
  }
  if (raw.includes('user already registered')) {
    return 'An account with this email already exists. Try logging in instead.';
  }
  return error.message || 'Something went wrong. Please try again.';
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      return null;
    }
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, full_name, role, membership, created_at')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Could not load profile:', error.message);
      setProfile(null);
      return null;
    }
    setProfile(data);
    return data;
  }, []);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      loadProfile(data.session?.user?.id).finally(() => {
        if (active) setLoading(false);
      });
    });

    // Fires on login, logout, token refresh, and when the Google redirect
    // lands back on the site.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!active) return;
      setSession(newSession);
      loadProfile(newSession?.user?.id);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signInWithPassword = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) throw new Error(readableAuthError(error));
  }, []);

  const signInWithGoogle = useCallback(async (returnTo = '/') => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}${returnTo}`,
        // Nudges Google Workspace users straight to their company account.
        // This is a hint only — the real enforcement is the database trigger.
        queryParams: { hd: 'stellarlab.tech' },
      },
    });
    if (error) throw new Error(readableAuthError(error));
  }, []);

  const signUp = useCallback(async (email, password, fullName) => {
    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: { full_name: (fullName || '').trim() },
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    if (error) throw new Error(readableAuthError(error));
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw new Error(readableAuthError(error));
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw new Error(readableAuthError(error));
  }, []);

  const updateFullName = useCallback(
    async (fullName) => {
      const userId = session?.user?.id;
      if (!userId) throw new Error('Not signed in.');

      // Column-level grant in migration 0001 means full_name is the only
      // column a user may change here. role and membership are admin-only.
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: fullName.trim() })
        .eq('id', userId);

      if (error) throw new Error(error.message);
      await loadProfile(userId);
    },
    [session, loadProfile],
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      loading,
      isAuthenticated: Boolean(session),
      isAdmin: profile?.role === 'admin',
      isAgent: profile?.membership === 'agent',
      // Display name, in the order the leaderboard uses. Never the email.
      displayName: profile?.full_name?.trim() || 'Agent',
      signInWithPassword,
      signInWithGoogle,
      signUp,
      signOut,
      requestPasswordReset,
      updatePassword,
      updateFullName,
      refreshProfile: () => loadProfile(session?.user?.id),
    }),
    [
      session,
      profile,
      loading,
      signInWithPassword,
      signInWithGoogle,
      signUp,
      signOut,
      requestPasswordReset,
      updatePassword,
      updateFullName,
      loadProfile,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
